import React from 'react'

import { checkMyConsentExists } from '@/api/consent.api'
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { readAuthFlag, readUser } from '@/store/services/local-storage.service'
import { doLogout, requireConsent } from '@/store/slices/auth-slice'
import { getActiveDataProcessingConsentData } from '@/store/slices/dataProcessingConsents-slice'
import { getProfileData } from '@/store/slices/profile-slice'
import { getActiveRodoPolicyData } from '@/store/slices/rodoPoliciesDocuments-slice'

export type ConsentBootstrapState = 'pending' | 'ready' | 'failed' | 'broken-session'

/** Pobranie wszystkiego, czego bramka potrzebuje do decyzji: roli z profilu, odpowiedzi
 *  o zgodzie i obu aktywnych dokumentów. Używają go strażnik i sam ekran `/consent`, bo
 *  strażnik nie obejmuje tej trasy — bez tego ekran zgody decydowałby na pustym store. */
export type ConsentBootstrap = {
  state: ConsentBootstrapState
  retry: () => void
}

export const useConsentBootstrap = (): ConsentBootstrap => {
  const [checked, setChecked] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)

  const dispatch = useAppDispatch()

  const profile = useAppSelector((state) => state.profile?.data)
  const profileStatus = useAppSelector((state) => state.profile.status)

  const brokenSession = readAuthFlag() && !readUser()

  React.useEffect(() => {
    if (brokenSession) {
      dispatch(doLogout())
      return
    }

    const userId = readUser()

    /* Bramka wstrzymuje render powłoki, a to ona pobiera profil na zimnym starcie —
       bez tego czekalibyśmy na rolę, której nikt już nie pobierze. */
    if (readAuthFlag() && userId && !profile && profileStatus === 'idle') dispatch(getProfileData(userId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (brokenSession) return undefined

    let active = true

    ;(async () => {
      try {
        const [hasConsent] = await Promise.all([
          checkMyConsentExists(),
          dispatch(getActiveRodoPolicyData()),
          dispatch(getActiveDataProcessingConsentData()),
        ])

        if (!active) return

        if (!hasConsent) dispatch(requireConsent())
      } catch {
        /* Nie odróżniamy tu rodzaju awarii — decyzję, czy wpuścić, podejmuje wywołujący
           na podstawie zwróconego `failed`. Komunikat pokazuje errorLoggingMiddleware. */
        if (active) setFailed(true)
      } finally {
        if (active) setChecked(true)
      }
    })()

    return () => {
      active = false
    }
  }, [attempt, brokenSession, dispatch])

  const retry = React.useCallback(() => {
    setChecked(false)
    setFailed(false)
    setAttempt((previous) => previous + 1)
  }, [])

  if (brokenSession) return { state: 'broken-session', retry }

  /* `Boolean(profile)`, a nie `status === 'succeeded'`: profil odświeżają w tle inne widoki,
     a warunek na statusie odmontowywałby wtedy całą aplikację i pokazywał ekran ładowania. */
  const profileKnown = Boolean(profile) || profileStatus === 'failed'

  if (!checked || !profileKnown) return { state: 'pending', retry }

  return { state: failed ? 'failed' : 'ready', retry }
}
